import { useState, useRef } from "react";
import { X, UploadCloud, Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AGE_GROUPS } from "@/lib/constants";
import { toast } from "sonner";

const MAX_WORDS = 150;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_BOOKS = 3;

function countWords(text) {
  return text.trim().split(/\s+/).filter((w) => w.length > 0).length;
}

const emptyBook = () => ({
  title: "",
  age_group: "",
  description: "",
  purchase_url: "",
  author_link: "",
  file: null,
  filePreview: null,
});

function BookCard({ index, book, onChange, onRemove, canRemove }) {
  const fileInputRef = useRef(null);
  const wordCount = countWords(book.description);
  const overWord = wordCount > MAX_WORDS;
  const [localError, setLocalError] = useState("");

  const handleFile = (f) => {
    setLocalError("");
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(f.type)) {
      setLocalError("Cover must be JPG or PNG.");
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setLocalError("Cover image exceeds 5MB.");
      return;
    }
    onChange({ ...book, file: f, filePreview: URL.createObjectURL(f) });
  };

  return (
    <div
      className="relative bg-[#F8F6F0]/60 border border-[#CFD6DC] rounded-lg p-5"
      data-testid={`book-card-${index}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wider text-[#0E232E]">
          Book {index + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-[#B23B28] hover:underline flex items-center gap-1"
            data-testid={`remove-book-${index}`}
          >
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Book title *
          </label>
          <input
            required
            className="field-input"
            value={book.title}
            onChange={(e) => onChange({ ...book, title: e.target.value })}
            data-testid={`input-title-${index}`}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Age group *
          </label>
          <select
            required
            className="field-input"
            value={book.age_group}
            onChange={(e) => onChange({ ...book, age_group: e.target.value })}
            data-testid={`input-age-group-${index}`}
          >
            <option value="">Select an age group…</option>
            {AGE_GROUPS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label} ({g.range})
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#0E232E]">
              Description *
            </label>
            <span
              className={`text-xs tabular-nums ${overWord ? "text-[#B23B28]" : "text-[#0E232E]/60"}`}
              data-testid={`word-count-${index}`}
            >
              {wordCount} / {MAX_WORDS} words
            </span>
          </div>
          <textarea
            required
            rows={3}
            className="field-input resize-y min-h-[90px]"
            value={book.description}
            onChange={(e) => onChange({ ...book, description: e.target.value })}
            data-testid={`input-description-${index}`}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Cover (JPG / PNG, ≤5MB) *
          </label>
          <div
            className="border-2 border-dashed border-[#CFD6DC] rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:border-[#38B6D2] transition-colors bg-white"
            onClick={() => fileInputRef.current?.click()}
            data-testid={`dropzone-${index}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`input-cover-${index}`}
            />
            {book.filePreview ? (
              <img
                src={book.filePreview}
                alt="Cover preview"
                className="w-14 h-20 object-cover rounded shadow-sm"
              />
            ) : (
              <div className="w-14 h-20 bg-[#F0F3F5] rounded flex items-center justify-center text-[#38B6D2]">
                <UploadCloud size={20} />
              </div>
            )}
            <div className="flex-1 text-sm text-[#2F2E2E]">
              {book.file ? (
                <>
                  <div className="text-[#0E232E] font-semibold truncate">{book.file.name}</div>
                  <div className="text-xs">Click to replace</div>
                </>
              ) : (
                <>
                  <div className="text-[#0E232E] font-semibold">Click to upload cover</div>
                  <div className="text-xs">JPG or PNG · up to 5MB</div>
                </>
              )}
            </div>
          </div>
          {localError && (
            <div className="mt-2 text-xs text-[#B23B28]">{localError}</div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
              Purchase link *
            </label>
            <input
              required
              type="url"
              placeholder="https://amazon.com/..."
              className="field-input"
              value={book.purchase_url}
              onChange={(e) => onChange({ ...book, purchase_url: e.target.value })}
              data-testid={`input-purchase-url-${index}`}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
              Your website (optional)
            </label>
            <input
              type="url"
              placeholder="https://..."
              className="field-input"
              value={book.author_link}
              onChange={(e) => onChange({ ...book, author_link: e.target.value })}
              data-testid={`input-author-link-${index}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SubmitModal({ open, onClose, onSubmitted }) {
  const [authorName, setAuthorName] = useState("");
  const [authorEmail, setAuthorEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [books, setBooks] = useState([emptyBook()]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setAuthorName("");
    setAuthorEmail("");
    setHoneypot("");
    setBooks([emptyBook()]);
    setError("");
    setProgress("");
  };

  const updateBook = (i, updated) =>
    setBooks(books.map((b, idx) => (idx === i ? updated : b)));
  const addBook = () =>
    books.length < MAX_BOOKS && setBooks([...books, emptyBook()]);
  const removeBook = (i) =>
    setBooks(books.filter((_, idx) => idx !== i));

  const validateAll = () => {
    if (!authorName.trim() || !authorEmail.trim())
      return "Your name and email are required.";
    for (let i = 0; i < books.length; i++) {
      const b = books[i];
      if (!b.title.trim()) return `Book ${i + 1}: title is required.`;
      if (!b.age_group) return `Book ${i + 1}: age group is required.`;
      if (!b.description.trim())
        return `Book ${i + 1}: description is required.`;
      if (countWords(b.description) > MAX_WORDS)
        return `Book ${i + 1}: description exceeds 150 words.`;
      if (!b.purchase_url.trim())
        return `Book ${i + 1}: purchase link is required.`;
      if (!b.file) return `Book ${i + 1}: cover image is required.`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validateAll();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      for (let i = 0; i < books.length; i++) {
        setProgress(`Submitting book ${i + 1} of ${books.length}…`);
        const b = books[i];
        const fd = new FormData();
        fd.append("author_name", authorName);
        fd.append("author_email", authorEmail);
        fd.append("title", b.title);
        fd.append("age_group", b.age_group);
        fd.append("description", b.description);
        fd.append("purchase_url", b.purchase_url);
        fd.append("author_link", b.author_link || "");
        fd.append("honeypot", honeypot);
        fd.append("cover_image", b.file);
        await api.post("/books/submit", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(
        books.length === 1
          ? "Thank you! Your book has been submitted for review."
          : `Thank you! ${books.length} books have been submitted for review.`
      );
      reset();
      onSubmitted?.();
      onClose();
    } catch (err) {
      setError(
        formatApiError(err.response?.data?.detail) || err.message || "Submission failed."
      );
    } finally {
      setSubmitting(false);
      setProgress("");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(14,35,46,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="submit-modal-overlay"
    >
      <div
        className="relative bg-white border border-[#CFD6DC] rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="submit-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close submit form"
          className="absolute top-4 right-4 text-[#0E232E]/60 hover:text-[#0E232E] transition-colors z-10"
          data-testid="submit-modal-close"
        >
          <X size={22} />
        </button>

        <div className="p-6 md:p-8">
          <h2
            id="submit-modal-title"
            className="font-display text-3xl md:text-4xl font-extrabold text-[#0E232E] leading-tight"
          >
            Add your book to the shelf
          </h2>
          <p className="mt-2 text-sm text-[#2F2E2E] leading-relaxed">
            Submit up to {MAX_BOOKS} books at a time. Each is reviewed within 3–5 business days. Your email stays private.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-6" data-testid="submit-form">
            {/* honeypot */}
            <input
              type="text"
              name="honeypot"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-5000px",
                width: 1,
                height: 1,
                opacity: 0,
              }}
            />

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
                  Your full name *
                </label>
                <input
                  required
                  className="field-input"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  data-testid="input-author-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
                  Your email *
                </label>
                <input
                  required
                  type="email"
                  className="field-input"
                  value={authorEmail}
                  onChange={(e) => setAuthorEmail(e.target.value)}
                  data-testid="input-author-email"
                />
              </div>
            </div>

            <div className="space-y-4">
              {books.map((b, i) => (
                <BookCard
                  key={i}
                  index={i}
                  book={b}
                  onChange={(updated) => updateBook(i, updated)}
                  onRemove={() => removeBook(i)}
                  canRemove={books.length > 1}
                />
              ))}
            </div>

            {books.length < MAX_BOOKS && (
              <button
                type="button"
                onClick={addBook}
                className="w-full px-4 py-3 rounded-lg border-2 border-dashed border-[#38B6D2]/60 text-[#2A95B0] font-bold text-sm uppercase tracking-wider hover:bg-[#38B6D2]/10 transition-colors flex items-center justify-center gap-2"
                data-testid="add-another-book"
              >
                <Plus size={16} /> Add another book ({books.length} of {MAX_BOOKS})
              </button>
            )}

            {error && (
              <div
                role="alert"
                className="text-sm text-[#B23B28] bg-[#FDEEEA] border border-[#F5BDAE] rounded-md p-3"
                data-testid="submit-error"
              >
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-md border border-[#CFD6DC] text-[#0E232E] font-semibold hover:bg-[#F0F3F5] transition"
                data-testid="submit-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-teal px-6 py-2.5 rounded-md flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="submit-confirm-btn"
              >
                {submitting && <Loader2 className="animate-spin" size={16} />}
                {submitting
                  ? progress || "Submitting…"
                  : `Submit ${books.length === 1 ? "book" : `${books.length} books`} for review`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
